import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp, staggerItem } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';
import Button from './Button';

const Products = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const [activeCategory, setActiveCategory] = useState('rebar');
  const [unitSystem, setUnitSystem] = useState('metric'); // 'metric' or 'imperial'
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerFixed, setIsDrawerFixed] = useState(false);
  const sectionRef = React.useRef(null);
  const drawerRef = React.useRef(null);

  const categories = [
    { id: 'rebar', name: 'Rebar', icon: '⚡' },
    { id: 'flats', name: 'Flat Bars', icon: '▬' },
    { id: 'angles', name: 'Angles', icon: '⌐' },
    { id: 'channels', name: 'Channels', icon: '⌒' },
    { id: 'coils', name: 'Coils', icon: '◯' }
  ];

  const productData = {
    rebar: {
      title: 'Deformed Steel Bars (Rebar)',
      description: 'High-strength reinforcement bars conforming to ASTM A615 and local standards.',
      specifications: [
        { 
          property: 'Diameter Range', 
          metric: '8mm - 40mm', 
          imperial: '5/16" - 1-5/8"' 
        },
        { 
          property: 'Yield Strength', 
          metric: '420 MPa (Grade 60)', 
          imperial: '60,000 psi (Grade 60)' 
        },
        { 
          property: 'Tensile Strength', 
          metric: '620 MPa min', 
          imperial: '90,000 psi min' 
        },
        { 
          property: 'Elongation', 
          metric: '12% min', 
          imperial: '12% min' 
        },
        { 
          property: 'Length Tolerance', 
          metric: '±25mm', 
          imperial: '±1"' 
        }
      ],
      grades: ['Grade 40', 'Grade 60', 'Grade 75'],
      applications: ['Concrete reinforcement', 'Foundation work', 'Bridge construction', 'High-rise buildings']
    },
    flats: {
      title: 'Hot Rolled Flat Bars',
      description: 'Precision flat steel bars for structural and fabrication applications.',
      specifications: [
        { 
          property: 'Thickness Range', 
          metric: '3mm - 50mm', 
          imperial: '1/8" - 2"' 
        },
        { 
          property: 'Width Range', 
          metric: '20mm - 300mm', 
          imperial: '3/4" - 12"' 
        },
        { 
          property: 'Yield Strength', 
          metric: '250-350 MPa', 
          imperial: '36,000-50,000 psi' 
        },
        { 
          property: 'Surface Finish', 
          metric: 'Hot rolled, pickled', 
          imperial: 'Hot rolled, pickled' 
        },
        { 
          property: 'Tolerance', 
          metric: '±0.5mm', 
          imperial: '±0.02"' 
        }
      ],
      grades: ['A36', 'A572', 'A992'],
      applications: ['Structural framing', 'Machine bases', 'Brackets and supports', 'General fabrication']
    },
    angles: {
      title: 'Structural Angle Steel',
      description: 'Equal and unequal leg angles for structural applications.',
      specifications: [
        { 
          property: 'Leg Size Range', 
          metric: '25x25 - 200x200mm', 
          imperial: '1x1 - 8x8"' 
        },
        { 
          property: 'Thickness Range', 
          metric: '3mm - 20mm', 
          imperial: '1/8" - 3/4"' 
        },
        { 
          property: 'Yield Strength', 
          metric: '250 MPa min', 
          imperial: '36,000 psi min' 
        },
        { 
          property: 'Length Standard', 
          metric: '6m, 9m, 12m', 
          imperial: '20\', 30\', 40\'' 
        },
        { 
          property: 'Straightness', 
          metric: '3mm/m max', 
          imperial: '1/8"/ft max' 
        }
      ],
      grades: ['A36', 'A572 Gr.50'],
      applications: ['Building frames', 'Transmission towers', 'Support structures', 'Bracing systems']
    },
    channels: {
      title: 'Steel Channels',
      description: 'Hot rolled channel sections for structural and architectural applications.',
      specifications: [
        { 
          property: 'Depth Range', 
          metric: '75mm - 400mm', 
          imperial: '3" - 16"' 
        },
        { 
          property: 'Flange Width', 
          metric: '40mm - 100mm', 
          imperial: '1.5" - 4"' 
        },
        { 
          property: 'Web Thickness', 
          metric: '5mm - 15mm', 
          imperial: '0.2" - 0.6"' 
        },
        { 
          property: 'Yield Strength', 
          metric: '250 MPa min', 
          imperial: '36,000 psi min' 
        },
        { 
          property: 'Standard Lengths', 
          metric: '6m, 9m, 12m', 
          imperial: '20\', 30\', 40\'' 
        }
      ],
      grades: ['A36', 'A572'],
      applications: ['Structural framing', 'Purlins', 'Door frames', 'Equipment supports']
    },
    coils: {
      title: 'Hot Rolled Coils',
      description: 'Wide steel coils for further processing and fabrication.',
      specifications: [
        { 
          property: 'Thickness Range', 
          metric: '1.5mm - 25mm', 
          imperial: '0.06" - 1"' 
        },
        { 
          property: 'Width Range', 
          metric: '600mm - 2000mm', 
          imperial: '24" - 80"' 
        },
        { 
          property: 'Coil Weight', 
          metric: '5-25 tonnes', 
          imperial: '11,000-55,000 lbs' 
        },
        { 
          property: 'Yield Strength', 
          metric: '250-550 MPa', 
          imperial: '36,000-80,000 psi' 
        },
        { 
          property: 'Surface Quality', 
          metric: 'Commercial, Drawing', 
          imperial: 'Commercial, Drawing' 
        }
      ],
      grades: ['A1011', 'A572', 'A992'],
      applications: ['Sheet metal fabrication', 'Automotive parts', 'Appliance manufacturing', 'Pipe production']
    }
  };

  const currentProduct = productData[activeCategory];

  // Handle sticky drawer behavior
  React.useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !drawerRef.current) return;
      
      const section = sectionRef.current;
      const sectionRect = section.getBoundingClientRect();
      const drawerOriginalRect = drawerRef.current.getBoundingClientRect();
      
      // Check if section is in viewport
      const sectionInView = sectionRect.top < window.innerHeight && sectionRect.bottom > 0;
      
      // Check if drawer should be fixed (when section top goes above viewport)
      const shouldBeFixed = sectionRect.top < 16 && sectionInView;
      
      setIsDrawerFixed(shouldBeFixed);
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Section id="products" background="dark" className="relative" ref={sectionRef}>
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
          Our <span className="text-primary-400">Products</span>
        </h2>
        <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed`}>
          Comprehensive range of steel products manufactured to exact specifications 
          with consistent quality and reliable supply.
        </p>
      </motion.div>

      {/* Mobile Sticky Drawer - Only visible on mobile */}
      <div className="lg:hidden">
        {/* Placeholder when drawer is fixed to maintain layout */}
        {isDrawerFixed && <div className="mb-8"></div>}
        
        {/* Dynamic Positioning Container */}
        <div 
          ref={drawerRef}
          className={`${
            isDrawerFixed 
              ? 'fixed left-4 right-4 z-50' 
              : 'relative mb-8'
          } transition-all duration-500 ease-out`}
          style={isDrawerFixed ? { 
            top: '60px',
            maxWidth: 'calc(100vw - 2rem)',
            margin: '0 auto'
          } : {}}
        >
          <motion.button
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            className={`w-full px-6 py-4 rounded-xl flex items-center justify-between transition-all duration-500 ${
              isDrawerFixed 
                ? 'bg-black/80 border border-primary-400/30 shadow-[0_8px_32px_rgba(0,0,0,0.3),0_0_0_1px_rgba(247,148,29,0.1),inset_0_1px_0_rgba(255,255,255,0.1)]' 
                : `${colors.bg.muted} shadow-lg`
            } backdrop-blur-md`}
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              <span className={`${colors.text.primary} font-medium`}>Filters & Units</span>
            </div>
            <motion.div
              animate={{ rotate: isDrawerOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </motion.button>

          {/* Mobile Drawer Content - Also sticky */}
          <AnimatePresence>
            {isDrawerOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className={`mt-4 p-6 ${colors.bg.card} rounded-xl border ${colors.border.default} shadow-lg backdrop-blur-sm`}>
                  {/* Unit System Toggle - Mobile */}
                  <div className="mb-6">
                    <h4 className={`${colors.text.primary} font-medium mb-3 text-sm uppercase tracking-wider`}>Units</h4>
                    <div className={`${colors.bg.muted} rounded-xl p-1 flex transition-colors duration-300`}>
                      <button
                        onClick={() => setUnitSystem('metric')}
                        className={`flex-1 px-4 py-2 rounded-lg transition-all duration-300 text-sm ${
                          unitSystem === 'metric' 
                            ? 'bg-primary-400 text-black font-semibold' 
                            : `${colors.text.secondary} hover:${colors.text.primary}`
                        }`}
                      >
                        Metric
                      </button>
                      <button
                        onClick={() => setUnitSystem('imperial')}
                        className={`flex-1 px-4 py-2 rounded-lg transition-all duration-300 text-sm ${
                          unitSystem === 'imperial' 
                            ? 'bg-primary-400 text-black font-semibold' 
                            : `${colors.text.secondary} hover:${colors.text.primary}`
                        }`}
                      >
                        Imperial
                      </button>
                    </div>
                  </div>

                  {/* Product Categories - Mobile */}
                  <div>
                    <h4 className={`${colors.text.primary} font-medium mb-3 text-sm uppercase tracking-wider`}>Products</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            setActiveCategory(category.id);
                            setIsDrawerOpen(false); // Close drawer when category is selected
                          }}
                          className={`px-4 py-3 rounded-xl transition-all duration-300 flex items-center space-x-2 text-sm ${
                            activeCategory === category.id
                              ? 'bg-primary-400 text-black font-semibold shadow-lg'
                              : `${colors.bg.muted} ${colors.text.secondary} hover:${colors.bg.hover} hover:${colors.text.primary}`
                          }`}
                        >
                          <span className="text-base">{category.icon}</span>
                          <span>{category.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop Unit System Toggle - Hidden on mobile */}
      <motion.div
        className="hidden lg:flex justify-center mb-8"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <div className={`${colors.bg.muted} rounded-xl p-1 flex transition-colors duration-300`}>
          <button
            onClick={() => setUnitSystem('metric')}
            className={`px-6 py-2 rounded-lg transition-all duration-300 ${
              unitSystem === 'metric' 
                ? 'bg-primary-400 text-black font-semibold' 
                : `${colors.text.secondary} hover:${colors.text.primary}`
            }`}
          >
            Metric
          </button>
          <button
            onClick={() => setUnitSystem('imperial')}
            className={`px-6 py-2 rounded-lg transition-all duration-300 ${
              unitSystem === 'imperial' 
                ? 'bg-primary-400 text-black font-semibold' 
                : `${colors.text.secondary} hover:${colors.text.primary}`
            }`}
          >
            Imperial
          </button>
        </div>
      </motion.div>

      {/* Desktop Product Categories - Hidden on mobile */}
      <motion.div
        className="hidden lg:flex flex-wrap justify-center gap-4 mb-12"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
      >
        {categories.map((category, index) => (
          <motion.button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center space-x-3 ${
              activeCategory === category.id
                ? 'bg-primary-400 text-black font-semibold shadow-magnetic'
                : `${colors.bg.muted} ${colors.text.secondary} hover:${colors.bg.hover} hover:${colors.text.primary}`
            }`}
            variants={fadeInUp}
            transition={{ delay: 0.4 + (index * 0.1) }}
          >
            <span className="text-xl">{category.icon}</span>
            <span>{category.name}</span>
          </motion.button>
        ))}
      </motion.div>


      {/* Product Details */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Product Info */}
          <div className="lg:col-span-1">
            <Card glass={true} className="p-8">
              <h3 className={`text-2xl font-bold ${colors.text.primary} mb-4`}>
                {currentProduct.title}
              </h3>
              <p className={`${colors.text.secondary} mb-6 leading-relaxed`}>
                {currentProduct.description}
              </p>
              
              {/* Grades */}
              <div className="mb-6">
                <h4 className={`${colors.text.primary} font-semibold mb-3`}>Available Grades</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProduct.grades.map((grade) => (
                    <span
                      key={grade}
                      className="px-3 py-1 bg-primary-400/20 text-primary-400 rounded-lg text-sm font-medium"
                    >
                      {grade}
                    </span>
                  ))}
                </div>
              </div>

              {/* Applications */}
              <div>
                <h4 className={`${colors.text.primary} font-semibold mb-3`}>Applications</h4>
                <ul className="space-y-2">
                  {currentProduct.applications.map((app, index) => (
                    <motion.li
                      key={app}
                      className={`flex items-center ${colors.text.secondary} text-sm`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-3"></div>
                      {app}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </Card>
          </div>

          {/* Specifications Table */}
          <div className="lg:col-span-2">
            <Card className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h4 className={`text-xl font-semibold ${colors.text.primary}`}>Technical Specifications</h4>
                <span className="text-primary-400 text-sm font-medium">
                  {unitSystem === 'metric' ? 'Metric Units' : 'Imperial Units'}
                </span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${colors.border.muted}`}>
                      <th className={`text-left ${colors.text.secondary} font-medium py-3 pr-4`}>Property</th>
                      <th className={`text-left ${colors.text.secondary} font-medium py-3`}>Specification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProduct.specifications.map((spec, index) => (
                      <motion.tr
                        key={spec.property}
                        className={`border-b ${colors.border.default} hover:${colors.bg.muted}/30 transition-colors`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <td className={`py-4 pr-4 ${colors.text.primary} font-medium`}>{spec.property}</td>
                        <td className={`py-4 ${colors.text.secondary}`}>
                          {unitSystem === 'metric' ? spec.metric : spec.imperial}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Download Spec Sheet */}
              <div className={`mt-8 pt-6 border-t ${colors.border.muted}`}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                  <div>
                    <h5 className={`${colors.text.primary} font-medium mb-1`}>Need detailed specifications?</h5>
                    <p className={`${colors.text.muted} text-sm`}>Download our complete technical datasheet</p>
                  </div>
                  <Button variant="secondary" size="sm">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* CTA Section */}
      <motion.div
        className="text-center mt-16"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
      >
        <Card glass={true} className="inline-block p-8 max-w-2xl">
          <h3 className={`text-xl font-semibold ${colors.text.primary} mb-4`}>
            Can't find what you're looking for?
          </h3>
          <p className={`${colors.text.secondary} mb-6`}>
            We offer custom steel solutions tailored to your specific requirements. 
            Contact our technical team for specialized grades and dimensions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              magnetic={true}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Request Custom Quote
            </Button>
            <Button 
              variant="ghost"
              onClick={() => document.querySelector('#capabilities')?.scrollIntoView({ behavior: 'smooth' })}
            >
              View Our Capabilities
            </Button>
          </div>
        </Card>
      </motion.div>
    </Section>
  );
};

export default Products;